/**
 * Selling milk at the gate (BR-23, P3-TR5).
 *
 * The delivery grid above records who *received* milk on the round; it moves no
 * money, because regular customers are billed monthly by invoice and the round
 * is something staff may write. This is the other half: milk sold for cash,
 * with a price and a destination account, which is what turns collected litres
 * into a figure on a balance.
 *
 * The endpoint has existed since P3 and no screen ever called it. That is why
 * milk could be collected, logged per animal and delivered in the app, and the
 * money for it recorded nowhere.
 *
 * The sale lands `pending`, like every other entry that reaches a balance
 * (BR-07). A partner selling at the gate is a maker; somebody else is still
 * the checker.
 */

import { toMoney, toMoneyString } from '@agrofarm/shared';
import { useState, type FormEvent } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAccounts } from '../finance/api.js';
import { useCustomers, useWalkinSale } from './api.js';

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const DECIMAL_DRAFT = /^\d{1,10}(\.\d{1,2})?$/;

/**
 * What the sale comes to, before it is sent.
 *
 * `toMoney` from `@agrofarm/shared` — the same Decimal the server multiplies
 * with in `recordWalkinSale`, not a reimplementation that happens to agree. A
 * partner checks this figure against what they were handed at the gate, so a
 * preview that disagreed with the saved amount by a paisa would be worse than
 * no preview at all.
 */
function previewTotal(litres: string, rate: string): string | null {
  if (!DECIMAL_DRAFT.test(litres) || !DECIMAL_DRAFT.test(rate)) return null;
  return toMoneyString(toMoney(litres).times(toMoney(rate)));
}

export function MilkSaleForm(): JSX.Element {
  const { t } = useTranslation();

  const { data: accounts = [] } = useAccounts();
  const { data: customers = [] } = useCustomers();
  const sell = useWalkinSale();

  const [litres, setLitres] = useState('');
  const [rate, setRate] = useState('');
  const [accountId, setAccountId] = useState<number | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);

  /*
   * A gate sale is cash in hand, so the cash account is the overwhelming
   * default. Chosen on first render rather than in an effect: there is nothing
   * to synchronise, only a starting value.
   */
  const defaultAccount = accounts.find((account) => account.type === 'cash') ?? accounts[0];
  const selectedAccount = accountId ?? defaultAccount?.id ?? null;

  /*
   * Regular customers are left out on purpose, and the server refuses them
   * too (AP-1). Their milk is billed monthly by invoice — selling it here as
   * well would bill the same litres twice.
   */
  const walkins = customers.filter((customer) => customer.type === 'walkin' && customer.isActive);

  const total = previewTotal(litres, rate);
  const canSubmit = total !== null && selectedAccount !== null && !sell.isPending;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});
    setSaved(null);

    try {
      const result = await sell.mutateAsync({
        date: todayInDhaka(),
        litres,
        rate,
        accountId: selectedAccount!,
        ...(customerId !== null ? { customerId } : {}),
      });

      // Only once the server has it (NFR-2).
      setSaved(result.amount);
      setLitres('');
      setRate('');
      setCustomerId(null);
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form className="mb-5 rounded-xl bg-white p-4 shadow-sm" onSubmit={(event) => void handleSubmit(event)} noValidate>
      <h2 className="mb-1 text-base font-semibold text-gray-800">{t('milk.sale.title')}</h2>
      <p className="mb-3 text-xs text-gray-500">{t('milk.sale.subtitle')}</p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="sale-litres">
            {t('milk.sale.litres')}
          </label>
          <input
            id="sale-litres"
            inputMode="decimal"
            value={litres}
            onChange={(event) => setLitres(event.target.value)}
            placeholder="0.00"
            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 tabular-nums"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="sale-rate">
            {t('milk.sale.rate')}
          </label>
          <input
            id="sale-rate"
            inputMode="decimal"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
            placeholder="0.00"
            className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 tabular-nums"
          />
        </div>
      </div>

      {fieldErrors['litres'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['litres']}</p>}
      {fieldErrors['rate'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['rate']}</p>}

      {/* The figure the partner checks against the notes in their hand. */}
      <div
        aria-live="polite"
        className="mt-3 rounded-xl bg-gray-50 px-4 py-3 text-right text-2xl font-semibold tabular-nums text-gray-900"
      >
        {total === null ? '—' : formatMoney(total)}
      </div>

      <fieldset className="mt-4">
        <legend className="mb-2 text-sm font-medium text-gray-700">{t('milk.sale.account')}</legend>
        <div className="flex flex-wrap gap-2">
          {accounts.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => setAccountId(account.id)}
              aria-pressed={selectedAccount === account.id}
              className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                selectedAccount === account.id
                  ? 'bg-green-700 font-medium text-white'
                  : 'bg-white text-gray-700 shadow-sm'
              }`}
            >
              {account.name}
            </button>
          ))}
        </div>
        {fieldErrors['accountId'] !== undefined && (
          <p className="mt-1 text-sm text-red-700">{fieldErrors['accountId']}</p>
        )}
      </fieldset>

      {walkins.length > 0 && (
        <fieldset className="mt-4">
          <legend className="mb-2 text-sm font-medium text-gray-700">{t('milk.sale.customer')}</legend>
          <div className="flex flex-wrap gap-2">
            {walkins.map((customer) => (
              <button
                key={customer.id}
                type="button"
                // Tapping the chosen one again clears it: most gate sales are
                // not worth recording as a person.
                onClick={() => setCustomerId(customerId === customer.id ? null : customer.id)}
                aria-pressed={customerId === customer.id}
                className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                  customerId === customer.id
                    ? 'bg-green-700 font-medium text-white'
                    : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                {customer.name}
              </button>
            ))}
          </div>
          {fieldErrors['customerId'] !== undefined && (
            <p className="mt-1 text-sm text-red-700">{fieldErrors['customerId']}</p>
          )}
        </fieldset>
      )}

      {errorKey !== null && (
        <div role="alert" className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      {saved !== null && errorKey === null && (
        <p role="status" className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-800">
          {t('milk.sale.saved', { amount: formatMoney(saved) })}
        </p>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className="mt-4 min-h-[52px] w-full rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
      >
        {t(sell.isPending ? 'milk.sale.saving' : 'milk.sale.submit')}
      </button>
    </form>
  );
}
